import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {
  doPostRegistrationRefresh,
  RegistrationActionOrigin,
  runRegistrationFlow,
} from 'notebook/integrations/registry/RegistrationActionUtil';
import {
  RegistrationStructuredTable,
  RegistrationStructuredTableAdapter,
} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata,
} from 'notebook/integrations/registry/RegistrationTableConstants';
import {ACTION_REGISTER_ROWS} from 'notebook/store/VersioningConstants';
import makeSubmitRowActionFunction from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {getBioentityIdsForExpirationConfirmationFromStructuredTable} from 'notebook/structured-tables/action-functions/registerActionFunction';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import {AxisContextMenuActionFunctionParameters} from 'notebook/structured-tables/StructuredTableConstants';
import {TableSubmissionDiffs} from 'notebook/structured-tables/util/datamods/getStructuredTableCellDiffOnSubmission';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {confirmExpirationDateEdits} from 'samples/valid-contents/ConfirmEntitiesExpirationDate';

import invokeStructuredTableMappings from '../structured-table-mappings/invokeStructuredTableMappings';

export const SUBMIT_REGISTRATION_ROW_ACTION_FUNCTION_CONFIG = {
  allowOutsideOfEntry: false,
  onSelectForSubmit: async (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<
      RegistrationStructuredTableMetadata,
      RegistrationStructuredTableRowMetadata
    >
  ): Promise<void> => {
    const entryId = actionFunctionParams.entryOrTemplateId!;
    const structuredTable = actionFunctionParams.structuredTable as RegistrationStructuredTable;
    const selection = {
      start: actionFunctionParams.selectionRange!.minIndex,
      end: actionFunctionParams.selectionRange!.maxIndex + 1,
    };

    const tagSchema = (
      actionFunctionParams.structuredTableAdapter as RegistrationStructuredTableAdapter
    ).getTagSchema(actionFunctionParams.structuredTable);
    assert(tagSchema != null, 'Registration table rows were submitted without a schema', {
      entryId,
    });

    const structuredTableRows = selection
      ? structuredTable.rows.slice(selection.start, selection.end)
      : structuredTable.rows;
    const slicedComputedTable = selection
      ? actionFunctionParams.computedTable.slice(selection.start, selection.end)
      : actionFunctionParams.computedTable;

    // If expiration date is being modified for entities, and there are existing containers of those entities,
    // only proceed if user confirms the action
    const bioentityIds = getBioentityIdsForExpirationConfirmationFromStructuredTable({
      structuredTableRows,
      structuredTableColumns: structuredTable.columns,
      structuredTableSchemas: structuredTable.schemas,
      computedTable: slicedComputedTable,
      metadata: structuredTable.metadata,
    });
    if (bioentityIds.length) {
      const {shouldProceed} = await confirmExpirationDateEdits(tagSchema.id, bioentityIds);
      if (!shouldProceed) {
        return;
      }
    }

    await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.RegistrationSubmitResponse>({
      entryId,
      loadingScreenMessage:
        actionFunctionParams.selectionRange!.minIndex !== actionFunctionParams.selectionRange!.maxIndex
          ? `Registering rows ${actionFunctionParams.selectionRange!.minIndex + 1} - ${
              actionFunctionParams.selectionRange!.maxIndex + 1
            }...`
          : `Registering row ${actionFunctionParams.selectionRange!.minIndex + 1}...`,
      autoSaverAction: ACTION_REGISTER_ROWS,
      doPerformAction: (
        tableSubmissionDiffs?: TableSubmissionDiffs,
        updateStatus?: (msg: string) => void,
        dataModsHeader?: DataModificationsHeader
      ) =>
        runRegistrationFlow(
          (
            allowMerging: boolean,
            selection,
            computedTable?: Array<Array<EvaluatedCell>>,
            tableSubmissionDiffs?,
            latestVersionId?: number
          ) =>
            ActionFunctionDAO.register(
              entryId,
              structuredTable,
              actionFunctionParams.computedTable.slice(selection!.start, selection!.end),
              actionFunctionParams.actionFunctionContext,
              tableSubmissionDiffs,
              selection,
              latestVersionId,
              {
                allowMerging,
              },
              dataModsHeader
            ),
          tagSchema,
          RegistrationActionOrigin.REGISTRATION_STRUCTURED_TABLE,
          selection,
          actionFunctionParams.structuredTable,
          actionFunctionParams.computedTable.slice(selection.start, selection.end),
          tableSubmissionDiffs,
          updateStatus
        ),
      doPostRefresh: async (actionResult) => {
        await doPostRegistrationRefresh(actionResult.responseData, entryId, structuredTable.provenanceUuid);
        invokeStructuredTableMappings(
          entryId,
          structuredTable,
          actionResult.resultType === ActionResultType.PARTIAL_SUCCESS
            ? actionResult.submittedRange
            : selection
        );
      },
      analyticsActionForPerfTracking: ELN_ANALYTICS_ACTIONS.REGISTERED_FROM_STRUCTURED_TABLE.action,
      structuredTables: [actionFunctionParams.structuredTable],
      structuredTableAdapters: [actionFunctionParams.structuredTableAdapter],
      computedTables: [actionFunctionParams.computedTable],
      tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
      selection,
    });
  },
};

export default makeSubmitRowActionFunction<
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata
>(SUBMIT_REGISTRATION_ROW_ACTION_FUNCTION_CONFIG);
